import Navigation from '@/components/feature/Navigation';
import SEOHead from '@/components/feature/SEOHead';
import { Link } from 'react-router-dom';
import { useScrollReveal, revealClass } from '@/hooks/useScrollReveal';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  '@id': 'https://oludamolaadebowale.com/about#profilepage',
  url: 'https://oludamolaadebowale.com/about',
  name: 'About | Oludamola Adebowale | Nigerian Cultural Historian, Curator & Archivist',
  description: 'Full biography, curriculum vitae, and professional background of Oludamola Adebowale, Nigerian cultural historian, curator, archivist, and Associate Fellow of the Royal Historical Society.',
  mainEntity: { '@id': 'https://oludamolaadebowale.com/#person' },
  about: { '@id': 'https://oludamolaadebowale.com/#person' },
  inLanguage: 'en',
  dateModified: '2026-04-11',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://oludamolaadebowale.com/' },
      { '@type': 'ListItem', position: 2, name: 'About', item: 'https://oludamolaadebowale.com/about' },
    ],
  },
};

export default function AboutPage() {
  const { ref: mainRef, isVisible: mainVisible } = useScrollReveal<HTMLDivElement>();
  const { ref: extraRef, isVisible: extraVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.05 });

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#2c2420' }}>
      <SEOHead
        title="About | Oludamola Adebowale | Nigerian Cultural Historian, Curator & Archivist CV"
        description="Full biography and CV of Oludamola Adebowale, Nigerian cultural historian, curator, and archivist. Associate Fellow of the Royal Historical Society, ICA Executive Committee Member, author of Fabric of a Nation and History of Lagos."
        keywords="Oludamola Adebowale biography, Nigerian cultural historian CV, AFRHistS archivist Nigeria, Lagos cultural heritage expert, Oludamola Adebowale about"
        canonical="https://oludamolaadebowale.com/about"
        ogType="profile"
        ogImage="https://static.readdy.ai/image/aed34eea4e280644dc241a74366b8cb3/a218dde90e537f98cb78b1a5590e8cdd.png"
        schemaJson={aboutSchema}
      />
      <Navigation />

      {/* ── COMBINED HERO + BIOGRAPHY (two-column) ── */}
      <section className="pt-28 pb-20" style={{ backgroundColor: '#2c2420' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div ref={mainRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-start ${revealClass(mainVisible)}`}>

            {/* LEFT: photo + identity */}
            <div className="flex flex-col gap-8">
              {/* Photo */}
              <div className="w-full rounded-lg overflow-hidden" style={{ backgroundColor: '#3d3028' }}>
                <img
                  src="https://storage.readdy-site.link/project_files/4630a528-7d86-42ab-a7d2-89b69416f273/fc3c1659-262f-4cf2-ae3e-f92264656772_Fanyogo-4939.jpg?v=a74bd6518f3f85a5150853ab266281ff"
                  alt="Oludamola Adebowale, Nigerian cultural historian, curator and archivist"
                  title="Oludamola Adebowale AFRHistS"
                  className="w-full h-auto object-contain"
                />
              </div>

              {/* Identity card below photo */}
              <div className="rounded-lg p-6" style={{ backgroundColor: '#3d3028' }}>
                <p className="text-xs uppercase tracking-widest text-[#b89a6a] mb-2 font-sans">About</p>
                <h1 className="font-serif text-3xl font-bold text-white leading-tight mb-3">
                  Oludamola Adebowale
                </h1>
                <p className="font-sans text-xs uppercase tracking-widest text-[#b89a6a] mb-6">
                  Archivist · Historian · Writer · Curator
                </p>

              </div>
            </div>

            {/* RIGHT: full biography */}
            <div className="flex flex-col gap-6">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#b89a6a] mb-3 font-sans">Biography</p>
                <h2 className="font-serif text-3xl lg:text-4xl font-bold text-white mb-6">Background &amp; Expertise</h2>
              </div>

              <div className="space-y-5 font-sans text-sm text-white/75 leading-relaxed">
                <p>
                  <strong className="text-white/75">Oludamola Adebowale, FRSA, AFRHistS</strong>
                </p>
                <p>
                  <strong className="text-white/75">Archivist, Historian, Writer, Curator, and Senior Curator, Nigerian-Brazilian Public History Project</strong>
                </p>
                <p>
                  Oludamola Adebowale, FRSA, AFRHistS, is a Nigerian archivist, public historian, writer, curator, and cultural heritage specialist dedicated to the preservation, documentation, and promotion of African history and cultural memory. He is a Fellow of the Royal Society of Arts (RSA), United Kingdom; an Associate Fellow of the Royal Historical Society (UK); and a Member of the International Council on Archives (ICA).
                </p>
                <p>
                  Adebowale studied Estate Management at <strong className="text-white/75">Obafemi Awolowo University</strong>, Ile-Ife (2008), Integrated Branding and Copywriting at Orange Academy (2009), and Business and Marketing Development at the Lagos Entrepreneurial Business School (2011). He further specialized in Copyright and Intellectual Property Law at the Institute of Advanced Legal Studies, University of Lagos (2015–2016), and completed the prestigious CopyrightX Program of <strong className="text-white/75">Harvard Law School</strong>.
                </p>
                <p>
                  Before fully dedicating himself to heritage preservation and public history, Adebowale served as Nigeria Country Manager for Instant Grass International, a trend forecasting and consumer insight development company based in Cape Town, South Africa, where he worked for five years. He has also consulted for international agencies including Space Doctors (United Kingdom), Ruth Somerfield Semiotics (London), and Matchboxology (South Africa).
                </p>
                <p>
                  He is the Founder and Creative Director of <strong className="text-white/75">ASIRI Magazine</strong>, widely regarded as one of Nigeria&apos;s foremost platforms dedicated to history, archival preservation, cultural heritage, and long-form historical storytelling. Through ASIRI Magazine, he has championed the preservation of historical records and promoted greater public engagement with Nigerian history. He is also a major contributor to the Arts and Culture pages of <em>Guardian Life</em>, the Sunday magazine of <em>The Guardian Nigeria</em>.
                </p>
                <p>
                  As an archivist and curator, Adebowale has managed and curated the ASIRI Magazine Archives for over thirteen years. He currently serves as Senior Curator of the <strong className="text-white/75">Nigerian-Brazilian Public History Project</strong>, an initiative committed to documenting, preserving, and promoting the history of the Afro-Brazilian community in Lagos and Brazil.
                </p>
                <p>
                  His expertise has earned him recognition both locally and internationally. He was among the select Nigerians chosen by the <strong className="text-white/75">Horniman Museum and Gardens (UK)</strong> for the <em>#ThenAndNow: Nigeria at 60</em> project. His work on indigenous language preservation was cited by France Info Afrique in discussions surrounding the disqualification of <em>Lionheart</em> from the 2020 Academy Awards International Feature Film category.
                </p>
                <p>
                  In 2021, he was consulted as one of the experts on Yorùbá culture and traditional religion for the Goethe-Institut Nigeria publication,{' '}
                  <em>Who&apos;s Who in the Yorùbá Pantheon</em>, alongside distinguished scholars and cultural custodians including Prof. Wale Adeniran, Oloye Muraina Oyelami, and Chief Ifayemi Elebuibon.
                </p>
                <p>
                  A passionate advocate for the intersection of history and technology, Adebowale developed <strong className="text-white/75">1851 Agidingbi Chess</strong> in 2021, the first Nigerian history-themed chess variant available both as a mobile application and physical board game. The game is inspired by the historical events surrounding the 1851 Bombardment of Lagos and is available on iOS and Android platforms.
                </p>
                <p>
                  On the 19th of October 2024, he was awarded an <strong className="text-white/75">Honorary Doctorate in Philosophy Honoris Causa</strong> in the Field of Public Administration and also an Integrity Fellow Humanitarian Award from the Anointed University, South Africa for his contributions in the Nigeria culture and arts space.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── EXTENDED BIOGRAPHY (full width, light bg) ── */}
      <section className="py-20" style={{ backgroundColor: '#f9f6f1' }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div ref={extraRef} className={`space-y-6 font-sans text-base text-gray-700 leading-relaxed ${revealClass(extraVisible)}`}>

            <p><strong>Curatorial Projects and Exhibitions</strong></p>
            <p>As a curator and heritage professional, Adebowale has conceived, curated, and produced numerous exhibitions, including:</p>
            <ul className="list-disc pl-8 space-y-1">
              <li>Colours of Our History: <em>1851 Bombardment of Lagos</em> (2018)</li>
              <li>Ogun State African Drum Festival Exhibition (2019)</li>
              <li>Chief Ebenezer Obey Museum Booth, Federal College of Education, Osiele, Abeokuta (2019)</li>
              <li>Wole Soyinka at 86: WSICE Birthday Exhibition (2019)</li>
              <li><em>Timeless Memories: Elastic Effects of Wole Soyinka</em> (2019–2026)</li>
              <li>British Council 75th Anniversary Virtual Exhibition (2020)</li>
              <li><em>Cycles of Fashion</em> - Terra Kulture and Federal Ministry of Information and Culture Project (2020)</li>
              <li><em>Vintage Nigeria Digital Campaign</em> in collaboration with the Rockefeller Archive Center and Ford Foundation (2020)</li>
              <li>Moses Majekodunmi Archive Exhibition, Lagos Polo Club (2023)</li>
              <li>Sijuwade Memorial Exhibition and Celebration (2025)</li>
              <li>In the Beginning there was Cloth (2025)</li>
            </ul>

            <p><strong>Participating Exhibitions</strong></p>
            <p>As an artist, researcher, and archivist, he has also participated in:</p>
            <ul className="list-disc pl-8 space-y-1">
              <li><em>Lagos: Ownership and Identities</em> - Institute of African and Diaspora Studies, University of Lagos, and Universität Bayreuth (2020)</li>
              <li><em>Nigeria Re-Envisaged</em> - Finland Government Historical Photography Exhibition (2024)</li>
            </ul>

            <p><strong>Film and Media Contributions</strong></p>
            <p>As a researcher and archivist, Adebowale contributed to the following film productions:</p>
            <ul className="list-disc pl-8 space-y-1">
              <li><em>Funmilayo Ransome-Kuti</em> (2024)</li>
              <li><em>The Man Died</em> (2024)</li>
              <li><em>Formation: The History of the LCCI</em> (2025)</li>
            </ul>
            <p>
              In October 2025, he was featured on{' '}
              <strong>CNN African Voices: Changemakers</strong> as one of the leading voices promoting Nigerian history and cultural heritage on the global stage.
            </p>

            <p><strong>Archive Digitization and Preservation Projects</strong></p>
            <p>Adebowale has led and supported significant archival preservation initiatives, including:</p>
            <ul className="list-disc pl-8 space-y-1">
              <li>Digitization of the Archive of Dr. Chief Moses Adekoyejo Majekodunmi (2022)</li>
              <li>Chief Dr. Ebenezer Obey-Fabiyi Museum Booth Project, Federal College of Education, Osiele, Abeokuta (2019)</li>
              <li>Consultancy for the John Randle Centre for Yorùbá Culture and History Museum</li>
            </ul>

            <p><strong>Professional Memberships and Recognition</strong></p>
            <ul className="list-disc pl-8 space-y-1">
              <li>Fellow, Royal Society of Arts (RSA), United Kingdom</li>
              <li>Associate Fellow, Royal Historical Society (UK)</li>
              <li>Member, International Council on Archives (ICA)</li>
              <li>Executive Committee Member, ICA Section on Literary Archives (2025–2029)</li>
            </ul>

            <p><strong>Publications</strong></p>
            <p>
              Adebowale is the author of{' '}
              <strong>The Fabric of a Nation: Essays on Nigeria&apos;s History, Arts and Culture</strong>, a collection of essays and scholarly reflections examining Nigerian history, heritage, identity, arts, and cultural development.
            </p>
            <p>
              Through his work as an archivist, curator, writer, and public historian, Oludamola Adebowale continues to champion the preservation of historical memory, ensuring that African stories, archives, and cultural legacies remain accessible to future generations.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: '#2c2420' }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">Work With Oludamola</h2>
          <p className="font-sans text-sm text-white/60 mb-8 max-w-xl mx-auto">
            Available for curatorial consultancy, archival projects, academic collaborations, keynote speaking, and press enquiries.
          </p>
          <Link
            to="/press#contact"
            className="inline-flex items-center gap-2 font-sans text-sm bg-[#b89a6a] text-white px-8 py-3 rounded-md hover:bg-[#a08558] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line" /> Get in Touch
          </Link>
        </div>
      </section>

      {/* Footer strip */}
      <div className="border-t border-white/10 py-8" style={{ backgroundColor: '#1e1814' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="font-sans text-xs text-gray-500">© 2026 Oludamola Adebowale. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/publications" className="font-sans text-xs text-gray-500 hover:text-white transition-colors">Publications</Link>
            <Link to="/press" className="font-sans text-xs text-gray-500 hover:text-white transition-colors">Press</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
